/*
 * /chart-maker — диаграмма по данным: таблица → столбцы, полосы, круг, кольцо,
 * линия, область, график по точкам; школьный режим рисует на тетрадной клетке
 * в масштабе и показывает расчёт (доли → проценты → градусы, значение → клетки).
 *
 * Данные — таблица на странице: вставка из Excel в любую клетку разливается по
 * таблице, вставка текста задачи разбирается на записи (parse.js). Рисует
 * render.js одним SVG на экран, PNG, SVG и PDF. Работа живёт в браузере
 * (__toolDraft.editor), делится ссылкой (lz-string в #c=…), кап — один
 * выданный файл ДИАГРАММЫ: та же диаграмма в другом формате или размере
 * бесплатна (ключ — содержимое, не формат).
 */
import { parseInput, localeDecimal, toCsv, decodeText, splitDelimited, detectDelimiter, normalizeGrid } from './parse.js';
import { freshState, upgradeState, buildModel, renderChart, renderNotebook, calcPage, FONT, TYPES, MAX_ROWS, MAX_SERIES } from './render.js';
import { stepDecimals } from './scale.js';
import { example } from './examples.js';
import { fitToCanvas } from '../map-studio/raster.js';

const SLUG = 'chart-maker';
const W = window.__CHARTMAKER || {};
const I = W.i18n || {};
const LANG = W.locale || 'en';
const LOC = (window.__intlLocale && window.__intlLocale()) || LANG;
const DEC = localeDecimal(LANG);
const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
const tr = (k, rep = {}) => String(I[k] != null ? I[k] : k).replace(/:([a-z_]+)/g, (m, n) => (rep[n] != null ? rep[n] : m));
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
function ae(action, extra) { try { window.__ae && window.__ae('feature_use', Object.assign({ slug: SLUG, action }, extra || {})); } catch (e) { /* optional */ } }
// The funnel (tool_stage; the last stage rides on tool_abandon): ready → editing → export → complete.
function stage(name, meta) { try { window.__stage && window.__stage(name, meta || {}); } catch (e) { /* optional */ } }
function toast(m, t) { try { window.__toast && window.__toast(m, t || 'info'); } catch (e) { /* optional */ } }
let editing = false;
function firstEdit(how) { if (editing) return; editing = true; stage('editing', { how }); }
/* Every change of the data bumps this. A file is read asynchronously (the first XLSX loads its
   reader: seconds on a phone), and a read that finishes after the person has pasted, picked an
   example or typed must not land on top of what they did since. */
let dataGen = 0;
const bumpData = () => ++dataGen;
const META = ['title', 'note', 'xTitle', 'yTitle', 'unit'];

// ── numbers and text in the reader's language ──────────────────────────────
const nfCache = {};
function fmt(v, d) {
    d = Math.max(0, Math.min(6, d | 0));
    if (!nfCache[d]) nfCache[d] = new Intl.NumberFormat(LOC, { maximumFractionDigits: d, minimumFractionDigits: 0 });
    return nfCache[d].format(v);
}
const mctx = document.createElement('canvas').getContext('2d');
const mcache = new Map();
/* Measured at 100 px and scaled: the notebook draws in millimetres (a 3-unit font), and a
   canvas measures a 3-px font with hinting that throws the width off by a third. */
function measure(text, size, bold) {
    const key = (bold ? 'b' : 'n') + '|' + text;
    let w = mcache.get(key);
    if (w === undefined) {
        mctx.font = (bold ? '700 ' : '400 ') + '100px ' + FONT;
        w = mctx.measureText(String(text)).width / 100;
        if (mcache.size > 5000) mcache.clear();
        mcache.set(key, w);
    }
    return w * size;
}
const ENV = { measure, fmt, t: tr, pointSep: DEC === ',' ? '; ' : ', ' };

// ── state, history ─────────────────────────────────────────────────────────
let S = freshState();
let model = null, out = null;
let drafts = null;
const hist = { stack: [], pos: -1 };
const snapshot = () => JSON.stringify(S);
let commitT = 0;
function commit(now) {
    clearTimeout(commitT);
    const go = () => {
        const s = snapshot();
        if (hist.stack[hist.pos] === s) return;
        hist.stack = hist.stack.slice(0, hist.pos + 1);
        hist.stack.push(s);
        if (hist.stack.length > 80) hist.stack.shift();
        hist.pos = hist.stack.length - 1;
        syncUndo();
        if (drafts) drafts.touch(true);
    };
    if (now) go(); else commitT = setTimeout(go, 450);
}
function syncUndo() {
    $('#cm-undo').disabled = hist.pos <= 0;
    $('#cm-redo').disabled = hist.pos >= hist.stack.length - 1;
}
function restore(s) { S = upgradeState(JSON.parse(s)); buildTable(); syncControls(); render(); syncUndo(); if (drafts) drafts.touch(true); }
function undo() { if (hist.pos > 0) { hist.pos--; restore(hist.stack[hist.pos]); ae('undo'); } }
function redo() { if (hist.pos < hist.stack.length - 1) { hist.pos++; restore(hist.stack[hist.pos]); ae('redo'); } }

// ── the table editor ───────────────────────────────────────────────────────
const table = () => $('#cm-table');

function buildTable() {
    const nC = S.cols.length;
    let h = '<thead><tr><th class="cm-rn" scope="col"></th>';
    for (let c = 0; c < nC; c++) {
        const ph = c === 0 ? tr('col_cat') : (nC > 2 ? tr('series_n', { n: c }) : tr('col_val'));
        h += '<th scope="col"><div class="cm-hcell"><input class="cm-in cm-h" size="' + (c ? 5 : 9) + '" data-h="' + c + '" value="' + esc(S.cols[c]) + '" placeholder="' + esc(ph) + '" aria-label="' + esc(ph) + '" maxlength="120">'
            + (c > 0 && nC > 2 ? '<button type="button" class="cm-x" data-delcol="' + c + '" aria-label="' + esc(tr('col_del')) + '" title="' + esc(tr('col_del')) + '">×</button>' : '') + '</div></th>';
    }
    h += '<th class="cm-rx" scope="col"></th></tr></thead><tbody>';
    S.rows.forEach((r, i) => {
        h += '<tr><td class="cm-rn">' + (i + 1) + '</td>';
        for (let c = 0; c < nC; c++) {
            // size=: an input's own width is ~20 characters, and a table of them is wider than a phone.
            h += '<td><input class="cm-in' + (c ? ' cm-num' : '') + '" size="' + (c ? 5 : 9) + '" data-r="' + i + '" data-c="' + c + '" value="' + esc(r[c]) + '"'
                + (c ? ' inputmode="decimal"' : '') + ' aria-label="' + esc(tr('cell_label', { r: i + 1, c: c ? (S.cols[c] || tr('col_val')) : tr('col_cat') })) + '" maxlength="120"></td>';
        }
        h += '<td class="cm-rx"><button type="button" class="cm-x" data-delrow="' + i + '" aria-label="' + esc(tr('row_del')) + '" title="' + esc(tr('row_del')) + '">×</button></td></tr>';
    });
    table().innerHTML = h + '</tbody>';
    markBad();
}

function markBad() {
    if (!model) return;
    $$('input.cm-bad', table()).forEach((x) => x.classList.remove('cm-bad'));
    model.bad.forEach((b) => { const x = table().querySelector('input[data-r="' + (b.row - 1) + '"][data-c="' + b.col + '"]'); if (x) x.classList.add('cm-bad'); });
}

function ensureSize(rows, cols) {
    cols = Math.min(MAX_SERIES + 1, cols);
    rows = Math.min(MAX_ROWS, rows);
    while (S.cols.length < cols) S.cols.push('');
    S.rows.forEach((r) => { while (r.length < S.cols.length) r.push(''); });
    while (S.rows.length < rows) S.rows.push(S.cols.map(() => ''));
}

function hasNumbers() { return S.rows.some((r) => r.slice(1).some((c) => String(c).trim() !== '')); }

/** A parsed table replaces the page's. */
function applyParsed(t, how) {
    if (!t || !t.rows.length) { toast(tr('paste_none'), 'info'); ae('paste_none', { how }); return false; }
    bumpData();
    const cols = t.cols.slice(0, MAX_SERIES + 1);
    while (cols.length < 2) cols.push('');
    S.cols = cols;
    S.rows = t.rows.slice(0, MAX_ROWS).map((r) => { const o = r.slice(0, cols.length); while (o.length < cols.length) o.push(''); return o; });
    // An example's title, note («Источник: NOAA»), axis names and unit described the example: a
    // pasted table under them was labelled «Площадь Мирового океана, млн км²» while it held rivers
    // in km. They are reset; labels the person typed stay.
    if (S.exMeta) { META.forEach((k) => { S[k] = ''; }); delete S.exMeta; }
    if (t.unit) S.unit = t.unit;
    // «Длины крупных рек России:» above the list names the chart, unless a title is already set.
    if (t.title && !String(S.title || '').trim()) S.title = t.title;
    buildTable(); syncControls(); render(); commit(true);
    // One message, the cut included: a separate «не больше 300 строк» was overwritten at once by
    // «легло строк: 300», and the person never learned that half the file stayed out.
    const cut = t.rows.length > MAX_ROWS || t.cols.length > MAX_SERIES + 1;
    toast(cut ? tr('pasted_cut', { n: S.rows.length, total: t.rows.length, s: S.cols.length - 1, stotal: t.cols.length - 1 })
        : t.kind === 'text' ? tr('pasted_text', { n: S.rows.length }) : tr('pasted', { n: S.rows.length }), cut ? 'info' : 'success');
    ae('paste', { how, kind: t.kind, rows: S.rows.length, cols: S.cols.length - 1, tr: t.transposed ? 1 : 0, cut: cut ? 1 : 0, title: t.title ? 1 : 0 });
    firstEdit('paste');
    return true;
}

function bindTable() {
    const tb = table();
    tb.addEventListener('input', (e) => {
        const x = e.target;
        if (x.dataset.h != null) S.cols[+x.dataset.h] = x.value;
        else if (x.dataset.r != null) S.rows[+x.dataset.r][+x.dataset.c] = x.value;
        else return;
        bumpData();
        firstEdit('type');
        schedule(); commit();
    });
    tb.addEventListener('click', (e) => {
        const dr = e.target.closest('[data-delrow]'), dc = e.target.closest('[data-delcol]');
        if (dr) {
            const i = +dr.dataset.delrow;
            S.rows.splice(i, 1);
            if (!S.rows.length) S.rows.push(S.cols.map(() => ''));
            buildTable(); render(); commit(true);
            const next = tb.querySelector('[data-delrow="' + Math.min(i, S.rows.length - 1) + '"]');
            if (next) next.focus();
        }
        if (dc) {
            const c = +dc.dataset.delcol;
            if (S.cols.length <= 2) return;
            S.cols.splice(c, 1);
            S.rows.forEach((r) => r.splice(c, 1));
            const colors = {};
            Object.keys(S.colors).forEach((k) => {
                const m = k.match(/^s(\d+)$/);
                if (!m) { colors[k] = S.colors[k]; return; }
                const j = +m[1];
                if (j < c - 1) colors[k] = S.colors[k]; else if (j > c - 1) colors['s' + (j - 1)] = S.colors[k];
            });
            S.colors = colors;
            buildTable(); syncControls(); render(); commit(true);
        }
    });
    tb.addEventListener('keydown', (e) => {
        const x = e.target;
        if (x.dataset.r == null) return;
        const r = +x.dataset.r, c = +x.dataset.c;
        let to = null;
        if (e.key === 'Enter' || (e.key === 'ArrowDown' && !e.shiftKey)) {
            if (r === S.rows.length - 1 && e.key === 'Enter') {
                if (S.rows.length >= MAX_ROWS) { toast(tr('rows_max', { n: MAX_ROWS }), 'info'); return; }
                S.rows.push(S.cols.map(() => '')); buildTable(); commit();
            }
            to = [r + 1, c];
        } else if (e.key === 'ArrowUp') to = [r - 1, c];
        if (!to) return;
        const n = tb.querySelector('input[data-r="' + to[0] + '"][data-c="' + to[1] + '"]');
        if (n) { e.preventDefault(); n.focus(); n.select(); }
    });
    tb.addEventListener('paste', (e) => {
        const x = e.target.closest('input[data-r], input[data-h]');
        if (!x) return;
        const text = (e.clipboardData || window.clipboardData).getData('text/plain') || '';
        if (!/[\t\n]/.test(text.trim())) return;          // one value — the browser pastes it
        e.preventDefault();
        const r = x.dataset.r != null ? +x.dataset.r : -1;
        const c = x.dataset.r != null ? +x.dataset.c : +x.dataset.h;
        // At the corner, or into an empty table: the whole thing, header and all, is
        // understood (header, wide layout, units). Elsewhere: the cells land where pasted,
        // as in a spreadsheet.
        if (!hasNumbers() || (r <= 0 && c === 0)) { applyParsed(parseInput(text, { dec: DEC, outDec: DEC }), 'cell'); return; }
        const d = detectDelimiter(text) || '\t';
        const grid = splitDelimited(text.replace(/\r\n?/g, '\n'), d);
        const startR = Math.max(0, r);
        ensureSize(startR + grid.length, c + Math.max(...grid.map((g) => g.length)));
        grid.forEach((g, i) => g.forEach((v, j) => {
            if (r === -1 && i === 0) { if (c + j < S.cols.length) S.cols[c + j] = v; return; }
            const rr = startR + i - (r === -1 ? 1 : 0);
            if (rr < S.rows.length && c + j < S.cols.length) S.rows[rr][c + j] = v;
        }));
        bumpData();
        buildTable(); syncControls(); render(); commit(true);
        ae('paste', { how: 'cells', rows: grid.length });
        firstEdit('paste');
    });
}

// ── rendering ──────────────────────────────────────────────────────────────
let rT = 0;
function schedule() { clearTimeout(rT); rT = setTimeout(render, 70); }

/* The preview is drawn for the width it is shown at, so its text has real pixel sizes
   (a 1000-wide chart in a 360-px phone column had 5-px labels); the files are drawn at
   their own size by exportOut(). */
let stageW = 0;
function render() {
    clearTimeout(rT);
    model = buildModel(S, { dec: DEC, t: tr, locale: LOC });
    const st = $('#cm-stage');
    stageW = st.clientWidth || 0;
    out = S.school ? renderNotebook(model, S, ENV) : renderChart(model, S, ENV, { width: stageW, minH: stageW < 600 ? 380 : 320 });
    st.classList.toggle('nb', !!S.school);
    st.innerHTML = out.svg || '';
    if (S.school && out.empty) st.innerHTML = '<p class="cm-empty">' + esc(tr('empty_chart')) + '</p>';
    markBad();
    renderMsgs();
    renderCalc();
    renderColors();
    noteIssues();
}

/* «Чем не понят»: which kinds of cells the page could not read and which warnings it showed —
   the class only, never the person's data. Reported once the table has settled (typing «12а»
   on the way to «12» is not an issue), and only when the set changes. */
let issueT = 0, cellSig = '';
const warned = new Set();
function cellClass(raw) {
    const s = String(raw);
    if (/\d[–—/-]\d/.test(s)) return 'range';
    if (/^\d{1,2}[./]\d{1,2}[./]\d{2,4}$/.test(s)) return 'date';
    if (/\d/.test(s) && /\p{L}/u.test(s)) return 'mixed';
    if (/\p{L}/u.test(s)) return 'text';
    return 'symbol';
}
function noteIssues() {
    clearTimeout(issueT);
    issueT = setTimeout(() => {
        if (!model) return;
        // The kinds of unread cells, when that set changes; each warning code once a visit.
        const cls = [...new Set(model.bad.map((b) => cellClass(b.raw)))].sort().join(',');
        if (cls !== cellSig) { cellSig = cls; if (cls) ae('bad_cells', { n: model.bad.length, cls }); }
        const warn = model.warnings.map((w) => w.code).concat(out && out.hidden && out.hidden.values ? ['values_hidden'] : []);
        warn.forEach((code) => { if (!warned.has(code)) { warned.add(code); ae('warning', { code, type: S.type }); } });
    }, 1500);
}

function renderMsgs() {
    const msgs = [];
    if (model.bad.length) msgs.push(tr('bad_cells', { list: model.bad.slice(0, 5).map((b) => '«' + b.raw + '»').join(', ') }));
    model.warnings.forEach((w) => {
        if (w.code === 'pie_first_series') msgs.push(tr('pie_first_series', { name: model.series[0].name || S.cols[1] || '1' }));
        else if (w.code === 'pie_nonpositive') msgs.push(tr('pie_nonpositive', { list: w.list.slice(0, 5).join(', ') }));
        else msgs.push(tr(w.code));
    });
    if (out && out.hidden && out.hidden.values) msgs.push(tr('values_hidden'));
    $('#cm-msgs').innerHTML = msgs.map((m) => '<p class="tc-msg warn">' + esc(m) + '</p>').join('');
}

// ── the school calculation ─────────────────────────────────────────────────
function calcRows() {
    const c = out && out.calc;
    if (!c) return null;
    const unit = model.unit ? '\u00A0' + model.unit : '';
    if (c.kind === 'pie') {
        const d = model.decimals;
        const head = [tr('calc_h_name'), tr('calc_h_value'), tr('calc_h_share'), tr('calc_h_pct'), tr('calc_h_deg'), tr('calc_h_draw')];
        const rows = c.rows.map((r) => [r.name || '—', fmt(r.v, d), fmt(r.v, d) + '\u00A0:\u00A0' + fmt(c.total, d) + ' ≈ ' + fmt(r.share, 4), fmt(r.pctR, 1) + '\u00A0%', fmt(r.deg, 1) + '°', r.degR + '°' + (r.adjusted ? '*' : '')]);
        rows.push([tr('calc_sum'), fmt(c.total, d), '1', fmt(c.rows.reduce((a, r) => a + r.pctR, 0), 1) + '\u00A0%', '360°', '360°']);
        const adj = c.rows.filter((r) => r.adjusted);
        const lines = [tr('calc_total', { total: fmt(c.total, d) + unit }), tr('calc_rule_pie')];
        const foot = adj.length ? tr('calc_adjusted', { sum: c.rows.reduce((a, r) => a + Math.round(r.deg), 0) }) : '';
        return { head, rows, lines, foot, widths: [44, 26, 46, 24, 24, 22] };
    }
    if (c.kind === 'bars') {
        const sd = stepDecimals(c.step);
        const multi = model.series.length > 1;
        const head = [tr('calc_h_name')].concat(multi ? [tr('calc_h_series')] : []).concat([tr('calc_h_value'), tr('calc_h_div'), tr('calc_h_cells')]);
        const rows = [];
        c.rows.forEach((r) => r.series.forEach((s, j) => {
            if (!s) return;
            rows.push([r.name || '—'].concat(multi ? [model.series[j].name] : []).concat([fmt(s.v, model.decimals), fmt(s.v, model.decimals) + '\u00A0:\u00A0' + fmt(c.step, sd) + ' = ' + fmt(s.exact, 2), fmt(s.half, 1)]));
        }));
        const lines = [tr('nb_scale', { step: fmt(c.step, sd) + unit }), tr('calc_rule_bars', { step: fmt(c.step, sd) })];
        return { head, rows, lines, foot: tr('calc_half'), widths: multi ? [40, 32, 28, 52, 24] : [52, 30, 64, 30] };
    }
    if (c.kind === 'plane' && c.yStep) {
        const lines = [tr('nb_scale_y', { step: fmt(c.yStep, stepDecimals(c.yStep)) + unit })];
        if (c.xStep) lines.push(tr('nb_scale_x', { step: fmt(c.xStep, stepDecimals(c.xStep)) }));
        else if (c.xCells) lines.push(tr('calc_plane_cats', { n: c.xCells }));
        return { head: null, rows: [], lines, foot: tr('calc_plane_rule') };
    }
    return null;
}

function renderCalc() {
    const box = $('#cm-calc');
    const c = S.school && model.cats.length ? calcRows() : null;
    box.hidden = !c;
    $('#cm-p-school').hidden = !S.school;
    if (!c) return;
    let h = '<p class="tc-eyebrow">' + esc(tr('calc_title')) + '</p>';
    h += c.lines.map((l) => '<p class="cm-calc-line">' + esc(l) + '</p>').join('');
    if (c.head) {
        h += '<div class="tc-tablewrap"><table class="tc-table cm-calc-t"><thead><tr>' + c.head.map((x, i) => '<th' + (i ? ' class="num"' : '') + '>' + esc(x) + '</th>').join('') + '</tr></thead><tbody>'
            // Numbers and formulas are isolated left-to-right: on an Arabic page «8,849 : 500 = 17.7»
            // otherwise reads backwards.
            + c.rows.map((r, k) => '<tr' + (k === c.rows.length - 1 && out.calc.kind === 'pie' ? ' class="cm-sum"' : '') + '>' + r.map((x, i) => (i ? '<td class="num"><bdi dir="ltr">' + esc(x) + '</bdi></td>' : '<td>' + esc(x) + '</td>')).join('') + '</tr>').join('') + '</tbody></table></div>';
    }
    if (c.foot) h += '<p class="tc-note">' + esc(c.foot) + '</p>';
    box.innerHTML = h;
    // the automatic scale, shown as the field's placeholder
    const sc = $('#cm-scale');
    const auto = out.calc.kind === 'bars' ? out.calc.step : out.calc.yStep;
    if (sc && auto) sc.placeholder = tr('scale_auto', { step: fmt(auto, stepDecimals(auto)) });
}

// ── colours ────────────────────────────────────────────────────────────────
function renderColors() {
    const box = $('#cm-colors');
    if (!box) return;
    const items = model.vary
        ? model.cats.map((c, i) => ({ key: 'c:' + c, name: c || '—', color: model.catColors[i] }))
        : model.series.map((s, j) => ({ key: 's' + j, name: s.name || tr('series_n', { n: j + 1 }), color: s.color }));
    const sig = items.map((x) => x.key + x.color).join('|');
    if (box.dataset.sig === sig) return;
    box.dataset.sig = sig;
    box.innerHTML = items.slice(0, 40).map((x) => '<label class="cm-color"><input type="color" data-color="' + esc(x.key) + '" value="' + esc(x.color) + '"><span>' + esc(x.name) + '</span></label>').join('');
}

// ── controls ───────────────────────────────────────────────────────────────
function syncControls() {
    $$('.cm-type').forEach((b) => { const on = b.dataset.type === S.type; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    const multi = S.cols.length > 2;
    $('#cm-stack-wrap').hidden = !(multi && (S.type === 'bar' || S.type === 'barh' || S.type === 'area'));
    $$('#cm-stack-wrap .tc-seg-b').forEach((b) => { const on = b.dataset.stack === S.stack; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    $('#cm-school').checked = !!S.school;
    $$('[data-k]').forEach((el) => {
        const k = el.dataset.k;
        if (el.type === 'checkbox') el.checked = !!S[k];
        else if (k === 'scaleCell') el.value = S.scaleCell ? String(S.scaleCell).replace('.', DEC) : '';
        else el.value = S[k] == null ? '' : String(S[k]);
    });
    $$('.cm-pal').forEach((b) => { const on = b.dataset.pal === S.palette; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    $$('.cm-theme .tc-seg-b').forEach((b) => { const on = b.dataset.theme === S.theme; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    const pie = S.type === 'pie' || S.type === 'donut';
    const show = (sel, on) => { const el = $(sel); if (el) el.hidden = !on; };
    show('#cm-f-pielabels', pie);
    show('#cm-f-connect', S.type === 'scatter');
    show('#cm-f-vary', !multi && (S.type === 'bar' || S.type === 'barh'));
    show('#cm-f-avg', !S.school && ['bar', 'barh', 'line', 'area'].includes(S.type) && !(multi && S.stack !== 'group' && S.type !== 'line'));
    show('#cm-f-xtitle', !pie);
    show('#cm-f-ytitle', !pie);
    show('#cm-f-grid', !pie);
    show('#cm-f-radius', S.school && pie);
    show('#cm-f-height', S.school && !pie);
    show('#cm-f-scale', S.school && !pie);
    show('#cm-f-paper', !S.school);
    show('#cm-nb-hint', !!S.school);
}

function setType(t) {
    if (!TYPES.includes(t) || t === S.type) return;
    S.type = t;
    syncControls(); render(); commit(true);
    ae('type', { type: t, school: S.school ? 1 : 0 });
    firstEdit('type_pick');
}

function bindControls() {
    $$('.cm-type').forEach((b) => b.addEventListener('click', () => setType(b.dataset.type)));
    $$('#cm-stack-wrap .tc-seg-b').forEach((b) => b.addEventListener('click', () => { S.stack = b.dataset.stack; syncControls(); render(); commit(true); ae('stack', { stack: S.stack }); }));
    $('#cm-school').addEventListener('change', (e) => {
        S.school = e.target.checked;
        if (S.school && !['bar', 'barh', 'pie', 'donut', 'line', 'scatter'].includes(S.type)) S.type = 'bar';
        syncControls(); render(); commit(true);
        ae('school', { on: S.school ? 1 : 0, type: S.type });
    });
    const cast = (el) => {
        const k = el.dataset.k;
        if (el.type === 'checkbox') return el.checked;
        if (k === 'font' || k === 'nbHeight' || k === 'nbRadius') return +el.value;
        if (k === 'scaleCell') { const v = parseFloat(String(el.value).replace(',', '.')); return v > 0 ? v : 0; }
        return el.value;
    };
    $$('[data-k]').forEach((el) => {
        const text = el.tagName === 'INPUT' && el.type === 'text';
        el.addEventListener(text ? 'input' : 'change', () => {
            S[el.dataset.k] = cast(el);
            // Typed by the person — the labels are theirs now and survive a new table.
            if (META.includes(el.dataset.k)) delete S.exMeta;
            if (text) { schedule(); commit(); } else { syncControls(); render(); commit(true); ae('style', { k: el.dataset.k }); }
        });
    });
    $$('.cm-pal').forEach((b) => b.addEventListener('click', () => { S.palette = b.dataset.pal; S.colors = {}; syncControls(); render(); commit(true); ae('palette', { p: S.palette }); }));
    $$('.cm-theme .tc-seg-b').forEach((b) => b.addEventListener('click', () => { S.theme = b.dataset.theme; syncControls(); render(); commit(true); ae('theme', { t: S.theme }); }));
    $('#cm-colors').addEventListener('input', (e) => { const x = e.target.closest('[data-color]'); if (!x) return; S.colors[x.dataset.color] = x.value; $('#cm-colors').dataset.sig = ''; schedule(); commit(); });
    $('#cm-colors-reset').addEventListener('click', () => { S.colors = {}; render(); commit(true); });
    // table tools
    $('#cm-add-row').addEventListener('click', () => {
        if (S.rows.length >= MAX_ROWS) { toast(tr('rows_max', { n: MAX_ROWS }), 'info'); return; }
        S.rows.push(S.cols.map(() => '')); buildTable(); commit(true);
        const x = table().querySelector('input[data-r="' + (S.rows.length - 1) + '"][data-c="0"]');
        if (x) x.focus();
    });
    $('#cm-add-col').addEventListener('click', () => {
        if (S.cols.length >= MAX_SERIES + 1) { toast(tr('series_max', { n: MAX_SERIES }), 'info'); return; }
        S.cols.push(''); S.rows.forEach((r) => r.push(''));
        buildTable(); syncControls(); render(); commit(true); ae('add_series');
        const x = table().querySelector('input[data-h="' + (S.cols.length - 1) + '"]');
        if (x) x.focus();
    });
    $('#cm-transpose').addEventListener('click', () => {
        const grid = [S.cols].concat(S.rows);
        const t = grid[0].map((_, j) => grid.map((r) => r[j] == null ? '' : r[j]));
        if (t.length - 1 > MAX_ROWS || t[0].length > MAX_SERIES + 1) { toast(tr('series_max', { n: MAX_SERIES }), 'info'); return; }
        S.cols = t[0]; S.rows = t.slice(1); S.colors = {};
        buildTable(); syncControls(); render(); commit(true); ae('transpose');
    });
    $('#cm-clear').addEventListener('click', () => {
        if (!hasNumbers() && !S.cols.some((c) => c)) return;
        bumpData();
        S.cols = ['', '']; S.rows = [['', ''], ['', ''], ['', '']]; S.colors = {}; S.unit = '';
        buildTable(); syncControls(); render(); commit(true); ae('clear');
    });
    // paste box
    $('#cm-paste-go').addEventListener('click', () => {
        const v = $('#cm-paste').value;
        if (applyParsed(parseInput(v, { dec: DEC, outDec: DEC }), 'box')) { $('#cm-paste').value = ''; $('#cm-paste-box').open = false; }
    });
    // examples
    $$('[data-ex]').forEach((b) => b.addEventListener('click', () => loadExample(b.dataset.ex)));
    // history, share, export
    $('#cm-undo').addEventListener('click', undo);
    $('#cm-redo').addEventListener('click', redo);
    document.addEventListener('keydown', (e) => {
        if (window.__keyOwnedByFocus && window.__keyOwnedByFocus(e)) return;
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') { e.preventDefault(); e.shiftKey ? redo() : undo(); }
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') { e.preventDefault(); redo(); }
    });
    $('#cm-share').addEventListener('click', share);
    $$('[data-export]').forEach((b) => b.addEventListener('click', () => doExport(b.dataset.export, b)));
    $('#cm-csv').addEventListener('click', () => {
        const csv = toCsv(S.cols.map((c, i) => c || (i ? tr('col_val') : tr('col_cat'))), S.rows.filter((r) => r.some((c) => String(c).trim())), DEC);
        window.__downloadFile(new Blob([csv], { type: 'text/csv;charset=utf-8' }), fileBase() + '.csv');
        ae('csv', { rows: S.rows.length });
    });
    // file: CSV, TSV, TXT, XLSX
    const zone = $('#cm-drop');
    if (zone && window.__bindUploaderFile) window.__bindUploaderFile(zone, (f) => { if (f) loadFile(f); });
    const fileBox = $('#cm-file-box');
    if (fileBox) fileBox.addEventListener('toggle', () => { if (fileBox.open) warmXlsx().catch(() => { /* read time will say */ }); });
}

function loadExample(key) {
    const months = Array.from({ length: 12 }, (_, m) => {
        try { return new Intl.DateTimeFormat(LOC, { month: 'short' }).format(new Date(2026, m, 15)).replace(/\.$/, ''); } catch (e) { return String(m + 1); }
    });
    const patch = example(key, I, months, DEC);
    if (!patch) return;
    bumpData();
    const keep = { theme: S.theme, aspect: S.aspect, font: S.font, palette: S.palette, school: S.school };
    S = upgradeState(Object.assign(freshState(), keep, patch));
    // The example's words describe the example's numbers: they go when the table is replaced.
    S.exMeta = key;
    if (S.school && S.type === 'area') S.type = 'line';
    buildTable(); syncControls(); render(); commit(true);
    ae('example', { ex: key });
    firstEdit('example');
}

/* The XLSX reader is three modules and a decompressor: fetched when the file box opens (the
   person is about to pick a file), the read itself takes milliseconds. */
let xlsxReader = null;
function warmXlsx() {
    if (!xlsxReader) xlsxReader = import('../file-converter/xlsx-read.js').catch((e) => { xlsxReader = null; throw e; });
    return xlsxReader;
}
async function loadFile(file) {
    const name = String(file.name || '');
    const gen = bumpData();
    const t0 = performance.now();
    // A read that takes longer than a blink says so: with no sign of life, the second tap on
    // «Открыть файл» was the person's only move.
    const slow = setTimeout(() => toast(tr('file_reading'), 'info'), 400);
    const xlsx = /\.xlsx$/i.test(name) || file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    try {
        let t;
        if (xlsx) {
            const { readXlsx } = await warmXlsx();
            const r = await readXlsx(file);
            const grid = (r.grid || []).map((row) => (row || []).map((c) => (c == null ? '' : typeof c === 'number' ? String(+c.toPrecision(12)).replace('.', DEC) : String(c))));
            t = Object.assign(normalizeGrid(grid, { dec: DEC, outDec: DEC }), { kind: 'table' });
        } else {
            t = parseInput(decodeText(new Uint8Array(await file.arrayBuffer())), { dec: DEC, outDec: DEC });
        }
        if (gen !== dataGen) { ae('file_stale', { how: xlsx ? 'xlsx' : 'csv', ms: Math.round(performance.now() - t0) }); return; }
        applyParsed(t, xlsx ? 'xlsx' : 'csv');
        ae('file_read', { how: xlsx ? 'xlsx' : 'csv', ms: Math.round(performance.now() - t0) });
    } catch (e) {
        if (gen === dataGen && window.__handleError) window.__handleError(e, 'chart_file_read', { say: tr('file_fail') });
    } finally {
        clearTimeout(slow);
    }
}

// ── share link, drafts ─────────────────────────────────────────────────────
function shareUrl() {
    const LZ = window.LZString;
    if (!LZ) return location.href.split('#')[0];
    return location.origin + location.pathname + '#c=' + LZ.compressToEncodedURIComponent(snapshot());
}
function fromHash() {
    const m = location.hash.match(/#c=([^&]+)/);
    if (!m || !window.LZString) return false;
    try {
        const s = window.LZString.decompressFromEncodedURIComponent(m[1]);
        if (!s) return false;
        S = upgradeState(JSON.parse(s));
        return true;
    } catch (e) { return false; }
}
function share() {
    const url = shareUrl();
    ae('share', { how: navigator.share && window.matchMedia('(pointer:coarse)').matches ? 'sheet' : 'copy' });
    // A phone hands the link to a messenger through the system sheet; a computer copies it.
    // No clipboard (an old WebView) — the link in a prompt to copy by hand, never silence.
    if (navigator.share && window.matchMedia('(pointer:coarse)').matches) { navigator.share({ title: document.title, url }).catch(() => { /* closed */ }); return; }
    const byHand = () => { try { window.prompt(tr('link_label'), url); } catch (e) { toast(tr('copy_fail'), 'error'); } };
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(url).then(() => toast(tr('link_copied'), 'success'), byHand);
    else byHand();
}
function contentKey() {
    // One chart = one unit of the daily cap: the same chart exported again (another size,
    // another format, copied) is free. Export settings are not part of the state.
    const s = snapshot();
    let h = 2166136261;
    for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
    return 'chart:' + (h >>> 0).toString(36);
}
function label() {
    if (S.title) return S.title;
    const named = S.rows.map((r) => String(r[0] || '').trim()).filter(Boolean).slice(0, 3).join(', ');
    return named || tr('file_default');
}
function draftBoot() {
    if (!window.__toolDraft || !window.__toolDraft.editor) return null;
    drafts = window.__toolDraft.editor({
        slug: SLUG, root: 'cmDrafts', icon: '📊', locale: LANG, debounceMs: 600,
        payload: () => ({ state: S, label: label(), preview: previewUrl() }),
        apply: (d) => { S = upgradeState(d.state); buildTable(); syncControls(); render(); hist.stack = [snapshot()]; hist.pos = 0; syncUndo(); return Promise.resolve([]); },
        fresh: () => { S = freshState(); buildTable(); syncControls(); render(); hist.stack = [snapshot()]; hist.pos = 0; syncUndo(); },
        started: () => hist.pos > 0,
        t: (k, f) => (I['drafts_' + k] != null ? I['drafts_' + k] : f),
        track: (a, d) => ae('draft_' + a, d), toast,
    });
    return drafts.boot();
}
function previewUrl() {
    if (!out || !out.svg || out.empty || out.svg.length > 60000) return '';
    return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(out.svg);
}

// ── export ─────────────────────────────────────────────────────────────────
function fileBase() {
    const t = String(S.title || tr('file_default')).trim();
    try { return window.__outputName ? window.__outputName.sanitize(t) : t.replace(/[\\/:*?"<>|]+/g, '_'); } catch (e) { return 'chart'; }
}
/** The chart at its file size (the preview may be drawn narrower for a phone). */
function exportOut() {
    return S.school ? out : renderChart(model, S, ENV);
}
function svgDims(svg) {
    const m = svg.match(/viewBox="0 0 ([\d.]+) ([\d.]+)"/);
    return m ? [+m[1], +m[2]] : [1000, 563];
}
async function makePng() {
    const side = +$('#cm-size').value || 2000;
    const transparent = !S.school && $('#cm-transparent').checked;
    const src = exportOut().svg;
    const [vw, vh] = svgDims(src);
    const { w, h, clamped } = fitToCanvas(vw, vh, side);
    if (clamped) { toast(tr('png_clamped', { px: Math.max(w, h) }), 'info'); ae('png_clamped', { want: side, got: Math.max(w, h) }); }
    let txt = src.replace(/^(<svg[^>]*?) width="[^"]*" height="[^"]*"/, '$1 width="' + w + '" height="' + h + '"');
    if (transparent) txt = txt.replace(/<rect[^>]*id="cm-bg"[^>]*\/>/, '');
    const url = URL.createObjectURL(new Blob([txt], { type: 'image/svg+xml' }));
    try {
        const img = new Image();
        img.decoding = 'async';
        await new Promise((res, rej) => { img.onload = res; img.onerror = () => rej(new Error('svg_image_failed')); img.src = url; });
        const cv = document.createElement('canvas');
        cv.width = w; cv.height = h;
        cv.getContext('2d').drawImage(img, 0, 0, w, h);
        return await window.__canvasBlob(cv, 'image/png');
    } finally { URL.revokeObjectURL(url); }
}
function svgEl(text) {
    const div = document.createElement('div');
    div.innerHTML = text;
    return div.firstElementChild;
}
async function makePdf() {
    const { mapPdf } = await import('../map-studio/pdf.js');
    const title = S.title || tr('file_default');
    if (S.school) {
        const pages = [svgEl(out.page)];
        const c = calcRows();
        if (c && c.head) pages.push(svgEl(calcPage(c.head, c.rows, c.widths, { env: ENV, title: tr('calc_title') + (S.title ? ' — ' + S.title : ''), lines: c.lines.concat(c.foot ? [c.foot] : []), boldLast: out.calc.kind === 'pie' })));
        // A4 exactly: the sheet is 190 × 277 mm inside 10-mm margins, so a cell prints 5 mm.
        return mapPdf(pages, { paper: 'a4', title, assets: W.assets || {}, top: true });
    }
    return mapPdf([svgEl(exportOut().svg)], { paper: $('#cm-paper').value, title, assets: W.assets || {} });
}
/* The first PDF fetches its library and fonts — 2–4 s on a phone with nothing on screen, and a
   second tap started a second file. While a file is being made the buttons say so and wait. */
let exporting = false;
function exportBusy(on, btn) {
    exporting = on;
    $$('[data-export]').forEach((b) => { b.disabled = on; b.classList.toggle('cm-busy', on && b === btn); });
    const box = $('.cm-p-export');
    if (box) box.setAttribute('aria-busy', on ? 'true' : 'false');
    if (on) { try { window.__announce && window.__announce(tr('exporting')); } catch (e) { /* optional */ } }
}
async function doExport(fmtKey, btn) {
    if (exporting) return;
    if (!model || !model.cats.length) { toast(tr('empty_chart'), 'info'); return; }
    const run = window.__toolLimit ? window.__toolLimit.begin(SLUG, contentKey()) : null;
    if (window.__toolLimit && !run) return;
    stage('export', { fmt: fmtKey });
    const t0 = performance.now();
    exportBusy(true, btn);
    try {
        let ok = false;
        if (fmtKey === 'copy') {
            // The promise goes into the ClipboardItem before any await: Safari allows a write
            // only inside the click, and a PNG takes longer than that to draw.
            if (!navigator.clipboard || !window.ClipboardItem) throw new Error('no_clipboard');
            await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': makePng() })]);
            toast(tr('img_copied'), 'success');
            ok = true;
        } else if (fmtKey === 'svg') {
            ok = await window.__downloadFile(new Blob([S.school ? out.page : exportOut().svg], { type: 'image/svg+xml' }), fileBase() + '.svg');
        } else if (fmtKey === 'pdf') {
            ok = await window.__downloadFile(await makePdf(), fileBase() + '.pdf');
        } else {
            ok = await window.__downloadFile(await makePng(), fileBase() + '.png');
        }
        if (ok === false) { if (run) run.abort(); return; }
        if (run) run.commit();
        if (drafts) drafts.exported();
        try { window.__aeToolComplete && window.__aeToolComplete({ fmt: fmtKey, type: S.type, n: model.cats.length, series: model.series.length, school: S.school ? 1 : 0, avg: S.avg ? 1 : 0, ms: Math.round(performance.now() - t0) }); } catch (e) { /* optional */ }
    } catch (e) {
        if (run) run.abort();
        if (window.__handleError) window.__handleError(e, fmtKey === 'copy' ? 'chart_copy_image' : 'chart_export', { say: fmtKey === 'copy' ? tr('img_copy_fail') : tr('export_fail') });
    } finally {
        exportBusy(false);
    }
}

// ── boot ───────────────────────────────────────────────────────────────────
function bindSettings() {
    if (!window.__toolSettings || !window.__toolSettings.bind) return;
    try {
        window.__toolSettings.bind(SLUG, $('#cm-size'), 'png_size', '2000');
        window.__toolSettings.bind(SLUG, $('#cm-paper'), 'paper', LANG === 'en' ? 'letter' : 'a4');
        window.__toolSettings.bind(SLUG, $('#cm-transparent'), 'transparent', false);
    } catch (e) { /* optional */ }
}

function watchWidth() {
    const st = $('#cm-stage');
    if (!st || !window.ResizeObserver) return;
    new ResizeObserver(() => { if (!S.school && Math.abs(st.clientWidth - stageW) > 2) schedule(); }).observe(st);
}

function boot() {
    const linked = fromHash();
    bindTable();
    bindControls();
    bindSettings();
    buildTable();
    syncControls();
    render();
    watchWidth();
    hist.stack = [snapshot()]; hist.pos = 0; syncUndo();
    const p = draftBoot();
    Promise.resolve(p).then(() => { if (linked && drafts && drafts.adopt) drafts.adopt(true); });
    stage('ready', { ms: Math.round(performance.now()), linked: linked ? 1 : 0 });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
